/**
 * =============================================================================
 * COOKIE PREFERENCES - Painel de Preferências de Cookies
 * =============================================================================
 *
 * Modal acessível para visualizar e alterar as preferências de cookies.
 * Utiliza o Dialog do shadcn/ui e o Switch do shadcn/ui.
 * Apresenta as 4 categorias e os serviços associados.
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, Shield, BarChart3, Megaphone, Settings } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useCookies } from "@/hooks/useCookies";
import {
  type CookieCategories,
  type CookieCategory,
  getServicesByCategory,
  categoryLabel,
  categoryDescription,
} from "@/utils/cookieManager";

const CATEGORY_ICONS: Record<CookieCategory, typeof Cookie> = {
  necessary: Shield,
  analytics: BarChart3,
  marketing: Megaphone,
  preferences: Settings,
};

const CATEGORY_ORDER: CookieCategory[] = [
  "necessary",
  "analytics",
  "marketing",
  "preferences",
];

export function CookiePreferences() {
  const {
    consent,
    isPreferencesOpen,
    closePreferences,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    language,
    t,
  } = useCookies();

  const [draft, setDraft] = useState<CookieCategories>(() => ({
    necessary: true,
    analytics: consent?.analytics ?? false,
    marketing: consent?.marketing ?? false,
    preferences: consent?.preferences ?? false,
  }));

  // Sincroniza o draft com o consentimento atual ao abrir
  useEffect(() => {
    if (isPreferencesOpen && consent) {
      const timer = requestAnimationFrame(() => {
        setDraft({ ...consent });
      });
      return () => cancelAnimationFrame(timer);
    }
    return undefined;
  }, [isPreferencesOpen, consent]);

  const handleToggle = (category: CookieCategory) => {
    if (category === "necessary") return;
    setDraft((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const handleSave = () => {
    savePreferences(draft);
  };

  return (
    <Dialog open={isPreferencesOpen} onOpenChange={closePreferences}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto bg-pureWhite border-seaMist">
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl text-deepOcean flex items-center gap-3">
            <Cookie size={28} className="text-oceanBlue" />
            {t.preferencesTitle}
          </DialogTitle>
          <DialogDescription className="text-textBody/70">
            {t.preferencesDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {CATEGORY_ORDER.map((category) => {
            const Icon = CATEGORY_ICONS[category];
            const isNecessary = category === "necessary";
            const services = getServicesByCategory(category);

            return (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="rounded-xl border border-seaMist p-4 bg-seaMist/30"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-oceanBlue/10 flex items-center justify-center flex-shrink-0">
                      <Icon size={20} className="text-oceanBlue" />
                    </div>
                    <div>
                      <h3 className="font-playfair text-base font-semibold text-deepOcean">
                        {categoryLabel(category, language)}
                      </h3>
                      <p className="text-sm text-textBody/70 mt-1">
                        {categoryDescription(category, language)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isNecessary ? (
                      <span className="text-xs font-medium text-oceanBlue bg-oceanBlue/10 px-2 py-1 rounded-full">
                        {t.alwaysActive}
                      </span>
                    ) : (
                      <Switch
                        id={`cookie-toggle-${category}`}
                        checked={draft[category]}
                        onCheckedChange={() => handleToggle(category)}
                        aria-labelledby={`cookie-label-${category}`}
                      />
                    )}
                  </div>
                </div>

                <AnimatePresence>
                  {draft[category] && services.length > 0 && !isNecessary && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <ul className="mt-3 pl-13 space-y-2 border-t border-seaMist pt-3">
                        {services.map((service) => (
                          <li
                            key={service.id}
                            className="text-sm text-textBody/80"
                          >
                            <span className="font-medium text-deepOcean">
                              {service.name}
                            </span>
                            : {service.description}
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Botões de ação */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mt-6 pt-4 border-t border-seaMist">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={acceptAll}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-oceanBlue text-pureWhite text-sm font-medium transition-all hover:bg-softSky focus:outline-none focus:ring-2 focus:ring-oceanBlue focus:ring-offset-2"
            >
              {t.acceptAll}
            </button>
            <button
              onClick={rejectNonEssential}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-transparent border border-textMuted/30 text-textBody text-sm font-medium transition-all hover:bg-seaMist focus:outline-none focus:ring-2 focus:ring-textMuted/50 focus:ring-offset-2"
            >
              {t.rejectNonEssential}
            </button>
          </div>

          <button
            onClick={handleSave}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-deepOcean text-pureWhite text-sm font-medium transition-all hover:bg-deepOcean/90 focus:outline-none focus:ring-2 focus:ring-deepOcean focus:ring-offset-2"
          >
            {t.savePreferences}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CookiePreferences;
